import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatPrice } from '../lib/utils';
import { useWishlist } from '../contexts/WishlistContext';
import SmartImage from './SmartImage';

export default function ProductCard({ product, index = 0 }) {
  const { has, toggle } = useWishlist();
  const wished = has(product.slug);
  const primary = product.images[0];
  const secondary = product.images[1] || product.images[0];
  const onSale = product.discount > 0;

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, ease: [0.85, 0, 0.15, 1], delay: (index % 4) * 0.06 }}
      className="group"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="block kb-parent relative overflow-hidden">
        <div className="relative aspect-[3/4] bg-muted overflow-hidden">
          <SmartImage
            src={primary}
            alt={product.name}
            className="absolute inset-0 kb-img"
            imgClassName="kb-img"
            aspect="3/4"
          />
          <img
            src={secondary}
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            width="800"
            height="1067"
            className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 ease-cinema group-hover:opacity-100"
          />
          <div className="absolute left-4 top-4 flex flex-col gap-1.5">
            {product.soldOut && (
              <span className="overline bg-foreground text-background px-2 py-1" data-testid={`sold-badge-${product.slug}`}>Sold Out</span>
            )}
            {!product.soldOut && product.newIn && !onSale && (
              <span className="overline bg-background/85 backdrop-blur-sm px-2 py-1">New</span>
            )}
            {!product.soldOut && onSale && (
              <span className="overline bg-foreground text-background px-2 py-1" data-testid={`sale-badge-${product.slug}`}>
                −{product.discount}%
              </span>
            )}
          </div>
          {product.soldOut && (
            <div aria-hidden className="absolute inset-0 bg-background/40 backdrop-blur-[1px] pointer-events-none" />
          )}
          <button
            type="button"
            aria-label={wished ? 'Remove from wishlist' : (product.soldOut ? 'Notify me — add to wishlist' : 'Add to wishlist')}
            data-testid={`wishlist-toggle-${product.slug}`}
            onClick={(e) => { e.preventDefault(); toggle(product.slug); }}
            className="absolute right-4 top-4 h-9 w-9 inline-flex items-center justify-center bg-background/80 backdrop-blur-sm hover:bg-background transition-colors duration-500"
          >
            <Heart className={`h-4 w-4 ${wished ? 'fill-current' : ''}`} />
          </button>
        </div>
      </Link>

      <div className="mt-5 flex items-baseline justify-between gap-4">
        <div>
          <Link to={`/product/${product.slug}`} className="text-[15px] leading-tight link-underline">{product.name}</Link>
          <p className="mt-1 overline text-muted-foreground flex items-center gap-2">
            {product.rating > 0 && (
              <span className="tabular">★ {product.rating.toFixed(1)}</span>
            )}
            {product.rating > 0 && <span className="opacity-40">·</span>}
            <span>{product.colors.length} {product.colors.length === 1 ? 'color' : 'colors'}</span>
          </p>
        </div>
        <div className="text-right whitespace-nowrap">
          {onSale ? (
            <>
              <p className="text-[15px] tabular">{formatPrice(product.salePrice, product.currency)}</p>
              <p className="overline text-muted-foreground line-through tabular">{formatPrice(product.price, product.currency)}</p>
            </>
          ) : (
            <p className="text-[15px] tabular">{formatPrice(product.price, product.currency)}</p>
          )}
        </div>
      </div>
    </motion.article>
  );
}
